import type {
  AnalysisState,
  ConditionalFormatRule,
  ConditionalOperator,
} from '@aibi/contracts';
import { Input, Select } from '@aibi/ui';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import {
  buildBooleanRules,
  buildPositiveNegativeRules,
  buildSemaphoreRules,
  CONDITIONAL_PRESET_LABELS,
  createBlankRule,
  OPERATOR_LABELS,
  type ConditionalPresetId,
} from '../../../visualization/conditional-format';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';
import { PropertyField, PropertyGroup } from './PropertyField';

const NUMERIC_OPS = new Set<ConditionalOperator>(['gt', 'gte', 'lt', 'lte', 'between']);

function fieldOptions(card: AnalysisState, catalog: ReturnType<typeof useWorkspace>['catalog']) {
  return [
    ...card.measures.map((measure) => ({
      key: measure.key,
      label: measure.label ?? catalog.get(measure.measureId)?.name ?? measure.key,
    })),
    ...card.dimensions.map((dimension) => ({
      key: dimension.key,
      label: dimension.label ?? catalog.get(dimension.dimensionId)?.name ?? dimension.key,
    })),
  ];
}

function summarizeRule(rule: ConditionalFormatRule): string {
  const op = OPERATOR_LABELS[rule.operator];
  if (rule.operator === 'isEmpty') return op;
  if (rule.operator === 'between') {
    return `${op} ${String(rule.values[0] ?? '')} y ${String(rule.values[1] ?? '')}`;
  }
  if (rule.operator === 'in') return `${op}: ${rule.values.map(String).join(', ')}`;
  return `${op} ${String(rule.values[0] ?? '')}`;
}

function parseOperand(raw: string): string | number | boolean {
  const trimmed = raw.trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed !== '' && Number.isFinite(Number(trimmed))) return Number(trimmed);
  return trimmed;
}

function operandText(value: string | number | boolean | undefined): string {
  if (value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

function valuesDraft(rule: ConditionalFormatRule): string {
  if (rule.operator === 'between') {
    return `${operandText(rule.values[0])};${operandText(rule.values[1])}`;
  }
  if (rule.operator === 'in') return rule.values.map(operandText).join(', ');
  return operandText(rule.values[0]);
}

function parseValuesDraft(
  operator: ConditionalOperator,
  raw: string,
): (string | number | boolean)[] {
  if (operator === 'isEmpty') return [];
  if (operator === 'between') {
    const [a, b] = raw.split(/[;|,]/).map((part) => part.trim());
    const low = Number(a);
    const high = Number(b);
    return [Number.isFinite(low) ? low : 0, Number.isFinite(high) ? high : 0];
  }
  if (operator === 'in') {
    return raw
      .split(',')
      .map((part) => parseOperand(part))
      .filter((part) => part !== '');
  }
  const value = parseOperand(raw);
  if (NUMERIC_OPS.has(operator) && typeof value !== 'number') return [0];
  return [value];
}

interface RuleColorPatch {
  readonly operator?: ConditionalOperator;
  readonly values?: readonly (string | number | boolean)[];
  readonly color?: string | null;
  readonly background?: string | null;
}

/** Editor de reglas IF/CASE y presets (semaforo, positivo/negativo, si/no). */
export function ConditionalFormatSection({ card }: { readonly card: AnalysisState }) {
  const { catalog } = useWorkspace();
  const { execute } = useWorkspaceActions();
  const fields = fieldOptions(card, catalog);
  const rules = card.visualization.formatting.conditional;
  const defaultKey = fields[0]?.key ?? '';

  const [fieldKey, setFieldKey] = useState(defaultKey);
  const [preset, setPreset] = useState<ConditionalPresetId | 'custom'>('semaforo');
  const [low, setLow] = useState('0');
  const [high, setHigh] = useState('100');

  if (fields.length === 0) return null;

  const activeKey = fields.some((field) => field.key === fieldKey) ? fieldKey : defaultKey;

  const commit = (next: readonly ConditionalFormatRule[]) => {
    execute({
      type: 'update-visualization',
      cardId: card.id,
      patch: { conditional: next },
    });
  };

  const applyPreset = () => {
    if (activeKey === '') return;
    if (preset === 'semaforo') {
      const floor = Number(low);
      const ceiling = Number(high);
      if (!Number.isFinite(floor) || !Number.isFinite(ceiling)) return;
      commit([
        ...rules.filter((rule) => rule.key !== activeKey),
        ...buildSemaphoreRules(activeKey, floor, ceiling),
      ]);
      return;
    }
    if (preset === 'positivo-negativo') {
      commit([
        ...rules.filter((rule) => rule.key !== activeKey),
        ...buildPositiveNegativeRules(activeKey),
      ]);
      return;
    }
    if (preset === 'si-no') {
      commit([...rules.filter((rule) => rule.key !== activeKey), ...buildBooleanRules(activeKey)]);
      return;
    }
    commit([...rules, createBlankRule(activeKey)]);
  };

  const updateRule = (id: string, patch: RuleColorPatch) => {
    commit(
      rules.map((rule) => {
        if (rule.id !== id) return rule;
        const color =
          patch.color === undefined ? rule.color : (patch.color ?? undefined);
        const background =
          patch.background === undefined ? rule.background : (patch.background ?? undefined);
        return {
          id: rule.id,
          key: rule.key,
          operator: patch.operator ?? rule.operator,
          values: patch.values === undefined ? [...rule.values] : [...patch.values],
          ...(color === undefined ? {} : { color }),
          ...(background === undefined ? {} : { background }),
        };
      }),
    );
  };

  return (
    <PropertyGroup title="Colores condicionales">
      <p className="text-[11px] leading-snug text-muted-foreground">
        Si se cumple la condicion, se aplica el color. La primera regla que coincida gana.
      </p>
      <PropertyField label="Campo">
        {(id) => (
          <Select
            id={id}
            value={activeKey}
            onChange={(event) => {
              setFieldKey(event.target.value);
            }}
          >
            {fields.map((field) => (
              <option key={field.key} value={field.key}>
                {field.label}
              </option>
            ))}
          </Select>
        )}
      </PropertyField>
      <PropertyField label="Plantilla">
        {(id) => (
          <Select
            id={id}
            value={preset}
            onChange={(event) => {
              setPreset(event.target.value as ConditionalPresetId | 'custom');
            }}
          >
            {(Object.keys(CONDITIONAL_PRESET_LABELS) as ConditionalPresetId[]).map((idPreset) => (
              <option key={idPreset} value={idPreset}>
                {CONDITIONAL_PRESET_LABELS[idPreset]}
              </option>
            ))}
            <option value="custom">Regla personalizada (IF)</option>
          </Select>
        )}
      </PropertyField>
      {preset === 'semaforo' && (
        <div className="grid grid-cols-2 gap-2">
          <PropertyField label="Rojo si &lt;">
            {(id) => (
              <Input
                id={id}
                type="number"
                value={low}
                onChange={(event) => {
                  setLow(event.target.value);
                }}
              />
            )}
          </PropertyField>
          <PropertyField label="Verde si ≥">
            {(id) => (
              <Input
                id={id}
                type="number"
                value={high}
                onChange={(event) => {
                  setHigh(event.target.value);
                }}
              />
            )}
          </PropertyField>
        </div>
      )}
      <button
        type="button"
        className="w-full rounded-md border border-border bg-muted/40 px-2 py-1.5 text-xs font-medium hover:bg-muted"
        onClick={applyPreset}
      >
        {preset === 'custom' ? 'Agregar regla' : 'Aplicar plantilla'}
      </button>

      {rules.length > 0 && (
        <ul className="space-y-2">
          {rules.map((rule) => {
            const fieldLabel = fields.find((field) => field.key === rule.key)?.label ?? rule.key;
            return (
              <li
                key={rule.id}
                className="space-y-1.5 rounded-md border border-border/80 bg-muted/20 p-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-[11px] font-medium">
                    {fieldLabel}: {summarizeRule(rule)}
                  </span>
                  <button
                    type="button"
                    className="shrink-0 text-[11px] text-destructive hover:underline"
                    onClick={() => {
                      commit(rules.filter((entry) => entry.id !== rule.id));
                    }}
                  >
                    Quitar
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <Select
                    aria-label="Operador"
                    value={rule.operator}
                    onChange={(event) => {
                      const operator = event.target.value as ConditionalOperator;
                      updateRule(rule.id, {
                        operator,
                        values: parseValuesDraft(operator, valuesDraft({ ...rule, operator })),
                      });
                    }}
                  >
                    {(Object.keys(OPERATOR_LABELS) as ConditionalOperator[]).map((operator) => (
                      <option key={operator} value={operator}>
                        {OPERATOR_LABELS[operator]}
                      </option>
                    ))}
                  </Select>
                  {rule.operator !== 'isEmpty' && (
                    <Input
                      aria-label="Valor"
                      key={`${rule.id}:${rule.operator}:${JSON.stringify(rule.values)}`}
                      defaultValue={valuesDraft(rule)}
                      placeholder={
                        rule.operator === 'between'
                          ? 'min;max'
                          : rule.operator === 'in'
                            ? 'a, b, c'
                            : 'valor'
                      }
                      className="font-mono text-xs"
                      onBlur={(event) => {
                        updateRule(rule.id, {
                          values: parseValuesDraft(rule.operator, event.target.value),
                        });
                      }}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <MiniColor
                    label="Texto"
                    value={rule.color ?? null}
                    onChange={(color) => {
                      updateRule(rule.id, { color });
                    }}
                  />
                  <MiniColor
                    label="Fondo"
                    value={rule.background ?? null}
                    onChange={(background) => {
                      updateRule(rule.id, { background });
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </PropertyGroup>
  );
}

function MiniColor({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
}) {
  const current = value ?? '#94A3B8';
  return (
    <label className="flex flex-1 cursor-pointer items-center gap-1.5 text-[10px] text-muted-foreground">
      <span
        className="size-5 shrink-0 rounded border border-border"
        style={{ backgroundColor: current } satisfies CSSProperties}
        title={value ?? 'Sin color'}
      />
      <input
        type="color"
        aria-label={label}
        value={current}
        className="sr-only"
        onChange={(event) => {
          onChange(event.target.value.toUpperCase());
        }}
      />
      <span>{label}</span>
      {value !== null && (
        <button
          type="button"
          className="ml-auto text-[10px] text-primary hover:underline"
          onClick={(event) => {
            event.preventDefault();
            onChange(null);
          }}
        >
          Quitar
        </button>
      )}
    </label>
  );
}
